import React, { useState, useEffect } from 'react';
import {
  X,
  Folder,
  Plus,
  Edit2,
  Trash2,
  Check,
  FolderPlus,
  Palette,
  AlertTriangle,
} from 'lucide-react';
import api from '../../api/client';

export const DEFAULT_CATEGORIES = [
  { id: 'cat-personal', name: 'Personal', color: '#3b82f6', description: 'Personal private notes and journal' },
  { id: 'cat-work', name: 'Work', color: '#6366f1', description: 'Office meetings, tasks, and workplace documentation' },
  { id: 'cat-finance', name: 'Finance', color: '#10b981', description: 'Banking details, statements, and tax notes' },
  { id: 'cat-projects', name: 'Projects', color: '#f59e0b', description: 'Active engineering projects and technical roadmaps' },
  { id: 'cat-security', name: 'Security', color: '#06b6d4', description: 'Credentials, recovery runbooks, and audit logs' },
  { id: 'cat-custom', name: 'Custom', color: '#ec4899', description: 'User-defined custom categorization' },
];

const PRESET_COLORS = [
  '#3b82f6', '#6366f1', '#10b981', '#f59e0b', '#06b6d4', '#ec4899',
  '#8b5cf6', '#14b8a6', '#f43f5e', '#64748b',
];

export default function NoteCategoryModal({
  open,
  onClose,
  categories = [],
  onUpdateCategories,
  notes = [],
}) {
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'create' | 'edit'
  const [editingCat, setEditingCat] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#3b82f6', description: '' });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Lock body scroll on mobile when modal is open
  useEffect(() => {
    if (open) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open) return null;

  const handleStartCreate = () => {
    setFormData({ name: '', color: PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)], description: '' });
    setError('');
    setActiveTab('create');
  };

  const handleStartEdit = (cat) => {
    setEditingCat(cat);
    setFormData({ name: cat.name, color: cat.color || '#3b82f6', description: cat.description || '' });
    setError('');
    setActiveTab('edit');
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Category name cannot be empty');
      return;
    }
    setSaving(true);
    setError('');

    try {
      if (activeTab === 'create') {
        const newCat = {
          id: `cat-${Date.now()}`,
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim(),
        };

        // If backend /categories API exists, sync it
        try {
          const res = await api.post('/categories', {
            name: newCat.name,
            color: newCat.color,
            description: newCat.description,
          });
          if (res.data?.id) newCat.id = res.data.id;
        } catch (apiErr) {
          console.warn('Backend categories sync fallback to local:', apiErr);
        }

        onUpdateCategories([...categories, newCat]);
      } else if (activeTab === 'edit' && editingCat) {
        const updated = categories.map((c) =>
          c.id === editingCat.id
            ? { ...c, name: formData.name.trim(), color: formData.color, description: formData.description.trim() }
            : c
        );

        try {
          await api.patch(`/categories/${editingCat.id}`, {
            name: formData.name.trim(),
            color: formData.color,
            description: formData.description.trim(),
          });
        } catch (apiErr) {
          console.warn('Backend categories update fallback to local:', apiErr);
        }

        onUpdateCategories(updated);
      }

      setActiveTab('list');
      setEditingCat(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (catId) => {
    try {
      try {
        await api.delete(`/categories/${catId}`);
      } catch (apiErr) {
        console.warn('Backend category delete fallback to local:', apiErr);
      }
      onUpdateCategories(categories.filter((c) => c.id !== catId));
      setDeleteConfirmId(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete category');
    }
  };

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel note-categories-modal animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Manage Categories"
      >
        <div className="modal-header">
          <div className="modal-header-info">
            <div className="modal-icon-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}>
              <Folder size={20} />
            </div>
            <div>
              <h2 className="modal-title">
                {activeTab === 'list' ? 'Manage Note Categories' : activeTab === 'create' ? 'New Category' : 'Edit Category'}
              </h2>
              <p className="modal-subtitle">Organize and classify your encrypted notes by project, team, or purpose.</p>
            </div>
          </div>
          <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="alert-banner alert-error" style={{ margin: '12px 24px 0 24px' }}>
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {activeTab === 'list' ? (
          <div className="modal-body note-categories-body">
            <div className="note-categories-top-bar">
              <span className="note-categories-count">{categories.length} Categories configured</span>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleStartCreate}
              >
                <Plus size={15} />
                <span>Add Category</span>
              </button>
            </div>

            <div className="note-categories-list">
              {categories.map((cat) => {
                const count = notes.filter((n) => (n.category || '').toLowerCase() === (cat.name || '').toLowerCase() && n.status !== 'TRASHED').length;
                const isConfirmingDelete = deleteConfirmId === cat.id;

                return (
                  <div key={cat.id} className="note-category-row">
                    <div className="note-category-row-left">
                      <span
                        className="note-category-color-dot"
                        style={{ background: cat.color || '#3b82f6' }}
                      />
                      <div>
                        <div className="note-category-name-wrap">
                          <strong className="note-category-name">{cat.name}</strong>
                          <span className="note-category-pill-count">{count} {count === 1 ? 'note' : 'notes'}</span>
                        </div>
                        {cat.description && (
                          <span className="note-category-row-desc">{cat.description}</span>
                        )}
                      </div>
                    </div>

                    <div className="note-category-row-actions">
                      {isConfirmingDelete ? (
                        <div className="note-category-delete-confirm">
                          <span>Delete?</span>
                          <button
                            type="button"
                            className="btn btn-danger btn-xs"
                            onClick={() => handleDeleteCategory(cat.id)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            onClick={() => setDeleteConfirmId(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn-icon btn-sm"
                            title="Edit Category"
                            onClick={() => handleStartEdit(cat)}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-sm btn-icon-danger"
                            title="Delete Category"
                            onClick={() => setDeleteConfirmId(cat.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="modal-footer" style={{ padding: '16px 0 0 0' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSaveCategory} className="modal-body note-categories-form">
            <div className="form-group">
              <label className="input-label" htmlFor="cat-name">Category Name *</label>
              <input
                id="cat-name"
                type="text"
                className="input-field"
                placeholder="e.g., Personal, Work, Finance, Projects"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={50}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="input-label">Category Color</label>
              <div className="color-presets-picker">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`color-preset-circle ${formData.color === c ? 'active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setFormData({ ...formData, color: c })}
                    aria-label={`Select color ${c}`}
                  >
                    {formData.color === c && <Check size={12} color="#fff" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="input-label" htmlFor="cat-desc">Description (Optional)</label>
              <input
                id="cat-desc"
                type="text"
                className="input-field"
                placeholder="Brief summary of notes in this category"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={200}
              />
            </div>

            <div className="modal-footer" style={{ padding: '16px 0 0 0' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setActiveTab('list');
                  setEditingCat(null);
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !formData.name.trim()}
              >
                {saving ? 'Saving...' : activeTab === 'create' ? 'Create Category' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
